import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Copy, CheckCircle, ArrowDownToLine, ArrowUpFromLine,
  X, AlertTriangle, Coins, RefreshCw, ExternalLink, Zap, Eye, EyeOff,
  Loader2, CheckCircle2, XCircle, Wifi, ArrowRightLeft, TrendingUp, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../providers/AuthProvider';
import { useGameWallet } from '../providers/GameWalletProvider';
import { usePriceOracle } from '../providers/PriceOracleProvider';
import { executeRealSwap, getSwapEstimate } from '../services/jupiterSwapService';
import InternalSwapModal from '../components/InternalSwapModal';
import QRCode from 'qrcode';

// $SLAP Token Contract - PHASE 11 REBRAND
const SLAP_TOKEN_MINT = 'A7rKF3srg28UArWp1ibVVUbQmnjUnitTtq7ZFR4tpump';

// ============================================================
// PHASE 11: SWAP FEE CONFIGURATION (REAL ON-CHAIN)
// ============================================================
const SWAP_FEE_PERCENT = 1.0; // 1% platform fee on swaps
const MIN_SWAP_SOL = 0.005; // Minimum swap amount (higher for real txs due to fees)

const WalletDashboard = () => {
  const navigate = useNavigate();
  const { walletAddress, localWallet, connected, createLocalWallet, solanaAddress, secretKey } = useAuth();
  const { 
    credits, 
    solBalance, 
    isRefreshing, 
    isWithdrawing,
    connectionStatus,
    rpcEndpoint,
    rpcUrl, // Actual RPC URL for Jupiter swaps
    lastBalanceFetch,
    manualRefresh,
    fetchSolBalance,
    withdrawSol,
    addCredits
  } = useGameWallet();
  
  // PHASE 11: Price Oracle for swap calculations
  const { tokenPrice, isLive, formatPrice, priceSource } = usePriceOracle();
  
  const [copied, setCopied] = useState(false);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [forceRefreshDone, setForceRefreshDone] = useState(false);
  
  // PHASE 14: IN-APP SWAP MODAL (No Redirects)
  const [showInternalSwapModal, setShowInternalSwapModal] = useState(false);
  
  // Legacy swap state (for fallback)
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapAmount, setSwapAmount] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapDirection, setSwapDirection] = useState('sol_to_slap'); // 'sol_to_slap' or 'slap_to_sol'
  const [swapProgress, setSwapProgress] = useState(null); // Track swap steps
  const [swapEstimate, setSwapEstimate] = useState(null); // Live quote from Jupiter
  const [lastSwapSignature, setLastSwapSignature] = useState(null);
  
  // Get the REAL Solana address
  const fullSolanaAddress = solanaAddress || localWallet?.publicKey;
  const displaySecretKey = secretKey || localWallet?.secretKey;
  
  // Short address for display
  const shortAddress = fullSolanaAddress 
    ? `${fullSolanaAddress.slice(0, 8)}...${fullSolanaAddress.slice(-8)}`
    : 'Not created';

  // FORCE REFRESH ON MOUNT - bypass any caching
  useEffect(() => {
    if (fullSolanaAddress && connectionStatus === 'connected' && !forceRefreshDone) {
      console.log('[WalletDashboard] 🔄 FORCE REFRESH on mount...');
      setForceRefreshDone(true);
      fetchSolBalance(true, true).then(balance => {
        console.log('[WalletDashboard] ✅ Force refresh complete. Balance:', balance);
      });
    }
  }, [fullSolanaAddress, connectionStatus, forceRefreshDone, fetchSolBalance]);

  // Generate QR code
  useEffect(() => {
    if (fullSolanaAddress && fullSolanaAddress.length >= 32) {
      QRCode.toDataURL(fullSolanaAddress, {
        width: 200,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' }
      }).then(url => {
        setQrCodeDataUrl(url);
      }).catch(err => {
        console.error('QR code generation failed:', err);
      });
    }
  }, [fullSolanaAddress]);

  // Copy to clipboard
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };

  // INSTANT WITHDRAWAL - No 24h wait
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (amount > solBalance) {
      toast.error('Insufficient SOL balance');
      return;
    }
    if (!withdrawAddress || withdrawAddress.length < 32) {
      toast.error('Enter a valid Solana address');
      return;
    }

    const result = await withdrawSol(withdrawAddress, amount);
    
    if (result.success) {
      setWithdrawAmount('');
      setWithdrawAddress('');
    }
  };

  // Create wallet
  const handleCreateWallet = async () => {
    setIsProcessing(true);
    try {
      await createLocalWallet();
      toast.success('Wallet created!');
    } catch (e) {
      toast.error('Failed to create wallet');
    } finally {
      setIsProcessing(false);
    }
  };

  const hasValidWallet = fullSolanaAddress && fullSolanaAddress.length >= 32;

  // ============================================================
  // PHASE 11: SWAP CALCULATIONS WITH FEE
  // ============================================================
  const calculateSwap = useCallback((amount, direction) => {
    if (!amount || isNaN(parseFloat(amount))) {
      return { tokensReceived: 0, fee: 0, feePercent: SWAP_FEE_PERCENT };
    }
    
    const inputAmount = parseFloat(amount);
    
    if (direction === 'sol_to_slap') {
      // SOL → $SLAP: User gets tokens based on current price
      // Formula: (SOL amount / token price) * (1 - fee)
      const grossTokens = inputAmount / tokenPrice;
      const fee = grossTokens * (SWAP_FEE_PERCENT / 100);
      const netTokens = Math.floor(grossTokens - fee);
      
      return {
        tokensReceived: netTokens,
        fee: Math.floor(fee),
        feePercent: SWAP_FEE_PERCENT,
        grossAmount: Math.floor(grossTokens),
        inputAmount: inputAmount,
        rate: Math.floor(1 / tokenPrice)
      };
    } else {
      // $SLAP → SOL: User gets SOL based on current price
      // Formula: (token amount * token price) * (1 - fee)
      const grossSol = inputAmount * tokenPrice;
      const fee = grossSol * (SWAP_FEE_PERCENT / 100);
      const netSol = grossSol - fee;
      
      return {
        solReceived: netSol,
        fee: fee,
        feePercent: SWAP_FEE_PERCENT,
        grossAmount: grossSol,
        inputAmount: inputAmount,
        rate: tokenPrice
      };
    }
  }, [tokenPrice]);
  
  // ============================================================
  // PHASE 11: REAL JUPITER SWAP WITH ON-CHAIN EXECUTION
  // ============================================================
  
  // Fetch live swap estimate when amount changes
  useEffect(() => {
    const fetchEstimate = async () => {
      if (!swapAmount || parseFloat(swapAmount) < MIN_SWAP_SOL || swapDirection !== 'sol_to_slap') {
        setSwapEstimate(null);
        return;
      }
      
      try {
        const estimate = await getSwapEstimate(parseFloat(swapAmount));
        setSwapEstimate(estimate);
      } catch (error) {
        console.error('Quote error:', error);
        setSwapEstimate(null);
      }
    };
    
    const debounce = setTimeout(fetchEstimate, 500);
    return () => clearTimeout(debounce);
  }, [swapAmount, swapDirection]);
  
  // Handle REAL SOL → $SLAP swap via Jupiter
  const handleSwap = async () => {
    const amount = parseFloat(swapAmount);
    
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    
    if (swapDirection === 'sol_to_slap') {
      if (amount < MIN_SWAP_SOL) {
        toast.error(`Minimum swap is ${MIN_SWAP_SOL} SOL`);
        return;
      }
      if (amount > solBalance) {
        toast.error('Insufficient SOL balance');
        return;
      }
      if (!displaySecretKey) {
        toast.error('Wallet secret key not found. Cannot sign transaction.');
        return;
      }
      
      // EMERGENCY FIX: Use HARDCODED Helius RPC URL
      // CRITICAL: Use Ankr - Browser/CORS friendly
      const swapRpcUrl = 'https://rpc.ankr.com/solana';
      
      setIsSwapping(true);
      setSwapProgress({ step: 'starting', message: 'Initializing swap...' });
      
      try {
        // Execute REAL on-chain swap
        const result = await executeRealSwap({
          solAmount: amount,
          userPublicKey: fullSolanaAddress,
          secretKeyBase58: displaySecretKey,
          rpcUrl: swapRpcUrl,
          slippageBps: 150, // 1.5% slippage for volatile tokens
          onProgress: (progress) => setSwapProgress(progress)
        });
        
        if (result.success) {
          setLastSwapSignature(result.signature);
          
          toast.success('Swap Complete! 🎉', {
            id: 'swap',
            description: (
              <div className="text-sm">
                <p>Received {result.outputAmount.toLocaleString()} $SLAP</p>
                <p className="text-xs text-white/60 mt-1">
                  Fee: {result.feeAmount.toFixed(4)} SOL ({SWAP_FEE_PERCENT}%)
                </p>
                <a 
                  href={result.explorerUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 text-xs hover:underline mt-1 block"
                >
                  View on Solscan →
                </a>
              </div>
            ),
            duration: 10000
          });
          
          // Refresh balances after successful swap
          await manualRefresh();
          
          setSwapAmount('');
          setSwapEstimate(null);
          setShowSwapModal(false);
        }
      } catch (error) {
        console.error('Jupiter swap error:', error);
        
        let errorMessage = error.message;
        if (error.message.includes('insufficient')) {
          errorMessage = 'Insufficient SOL for swap + network fees';
        } else if (error.message.includes('route')) {
          errorMessage = 'No swap route available. Token may have low liquidity.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Transaction timed out. Please try again.';
        }
        
        toast.error('Swap Failed', {
          id: 'swap',
          description: errorMessage,
          duration: 8000
        });
      } finally {
        setIsSwapping(false);
        setSwapProgress(null);
      }
    } else {
      // $SLAP → SOL: Redirect to Jupiter (requires token account setup)
      toast.error('$SLAP → SOL requires external swap', {
        description: 'Use Jupiter DEX to sell $SLAP tokens',
        action: {
          label: 'Open Jupiter',
          onClick: () => window.open(`https://jup.ag/swap/${SLAP_TOKEN_MINT}-SOL`, '_blank')
        }
      });
    }
  };

  // Connection status indicator
  const ConnectionIndicator = () => {
    const statusConfig = {
      disconnected: { color: 'text-gray-400', icon: XCircle, text: 'Disconnected' },
      connecting: { color: 'text-yellow-400', icon: Loader2, text: 'Connecting...' },
      connected: { color: 'text-green-400', icon: Wifi, text: 'Mainnet-Beta' },
      error: { color: 'text-red-400', icon: XCircle, text: 'Connection Error' },
    };
    const config = statusConfig[connectionStatus] || statusConfig.disconnected;
    const Icon = config.icon;
    
    return (
      <div className={`flex items-center gap-1.5 text-xs ${config.color}`}>
        <Icon className={`w-3 h-3 ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} />
        <span>{config.text}</span>
      </div>
    );
  };

  // RPC Debug Status Indicator (bottom corner)
  const RpcDebugIndicator = () => {
    const isHelius = rpcEndpoint === 'Helius';
    const statusColor = isHelius ? 'bg-green-500' : 'bg-red-500';
    const statusText = isHelius ? 'RPC: Custom (Helius)' : 'RPC: Error';
    
    return (
      <div 
        className="fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-black/80 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5 text-xs"
        data-testid="rpc-debug-indicator"
      >
        <div className={`w-2 h-2 rounded-full ${statusColor} animate-pulse`} />
        <span className="text-white/80 font-mono">{statusText}</span>
        {lastBalanceFetch && (
          <span className="text-white/40 text-[10px]">
            {new Date(lastBalanceFetch).toLocaleTimeString()}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-24">
      {/* PHASE 11: SWAP MODAL */}
      <AnimatePresence>
        {showSwapModal && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSwapModal(false)}
          >
            <motion.div
              className="bg-[#0a0a0a] border-2 border-green-500/50 rounded-2xl p-6 max-w-md w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-anton text-2xl text-white flex items-center gap-2">
                  <ArrowRightLeft className="w-6 h-6 text-green-400" />
                  SWAP
                </h2>
                <button onClick={() => setShowSwapModal(false)} className="text-white/60 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Live Price Indicator */}
              <div className="bg-black/50 rounded-lg p-3 mb-4 flex items-center justify-between">
                <span className="text-white/60 text-sm">$SLAP Price</span>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
                  <span className="font-mono text-green-400">{formatPrice(tokenPrice)}</span>
                  <span className="text-xs text-white/40">({priceSource})</span>
                </div>
              </div>
              
              {/* Swap Direction Toggle */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  onClick={() => setSwapDirection('sol_to_slap')}
                  className={`py-3 rounded-lg font-bold text-sm transition-all ${
                    swapDirection === 'sol_to_slap'
                      ? 'bg-green-500/30 border-2 border-green-500 text-green-400'
                      : 'bg-white/5 border-2 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  SOL → $SLAP
                </button>
                <button
                  onClick={() => setSwapDirection('slap_to_sol')}
                  className={`py-3 rounded-lg font-bold text-sm transition-all ${
                    swapDirection === 'slap_to_sol'
                      ? 'bg-purple-500/30 border-2 border-purple-500 text-purple-400'
                      : 'bg-white/5 border-2 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  $SLAP → SOL
                </button>
              </div>
              
              {/* Input Amount */}
              <div className="mb-4">
                <label className="text-white/60 text-sm mb-2 block">
                  {swapDirection === 'sol_to_slap' ? 'SOL Amount' : '$SLAP Amount'}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg">
                    {swapDirection === 'sol_to_slap' ? '◎' : '🪙'}
                  </span>
                  <input
                    type="number"
                    step={swapDirection === 'sol_to_slap' ? '0.001' : '100'}
                    value={swapAmount}
                    onChange={(e) => setSwapAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white text-xl font-bold placeholder:text-white/30 focus:border-green-500 focus:outline-none"
                    data-testid="swap-amount-input"
                  />
                </div>
                <p className="text-white/40 text-xs mt-1">
                  Available: {swapDirection === 'sol_to_slap' 
                    ? `${solBalance.toFixed(4)} SOL` 
                    : `${credits.toLocaleString()} $SLAP`}
                </p>
              </div>
              
              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {(swapDirection === 'sol_to_slap' 
                  ? [0.01, 0.05, 0.1, solBalance]
                  : [1000, 5000, 10000, credits]
                ).map((val, i) => (
                  <button
                    key={i}
                    onClick={() => setSwapAmount(i === 3 ? val.toString() : val.toString())}
                    className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/80 text-xs font-bold transition-colors"
                  >
                    {i === 3 ? 'MAX' : (swapDirection === 'sol_to_slap' ? val : val.toLocaleString())}
                  </button>
                ))}
              </div>
              
              {/* Swap Preview - REAL Jupiter Quote */}
              {swapAmount && parseFloat(swapAmount) >= MIN_SWAP_SOL && swapDirection === 'sol_to_slap' && (
                <motion.div
                  className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl p-4 mb-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {swapEstimate ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white/60 text-sm">You'll receive (live quote)</span>
                        <span className="font-anton text-xl text-green-400">
                          {swapEstimate.outputTokens.toLocaleString()} $SLAP
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-white/40">Platform Fee ({SWAP_FEE_PERCENT}%)</span>
                        <span className="text-red-400">
                          -{swapEstimate.feeSol.toFixed(6)} SOL
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-white/40">Net Swap Amount</span>
                        <span className="text-white/60">
                          {swapEstimate.netSol.toFixed(6)} SOL
                        </span>
                      </div>
                      {swapEstimate.route && (
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-white/40">Route</span>
                          <span className="text-blue-400 text-[10px]">
                            {swapEstimate.route}
                          </span>
                        </div>
                      )}
                      {parseFloat(swapEstimate.priceImpact) > 1 && (
                        <div className="mt-2 p-2 bg-red-500/20 border border-red-500/50 rounded-lg">
                          <span className="text-red-400 text-xs">
                            ⚠️ High price impact: {swapEstimate.priceImpact}%
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-green-400 mr-2" />
                      <span className="text-white/60 text-sm">Fetching live quote...</span>
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Swap Progress Indicator */}
              {isSwapping && swapProgress && (
                <motion.div
                  className="bg-blue-900/30 border border-blue-500/50 rounded-xl p-4 mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                    <div>
                      <p className="text-blue-400 font-bold text-sm">
                        {swapProgress.step.toUpperCase()}
                      </p>
                      <p className="text-white/60 text-xs">
                        {swapProgress.message}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {/* Fee Disclosure - REAL */}
              <div className="flex items-start gap-2 text-xs text-white/40 mb-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-yellow-500" />
                <span>
                  <strong className="text-yellow-400">{SWAP_FEE_PERCENT}% platform fee</strong> is deducted BEFORE the swap. 
                  This is a REAL on-chain transaction via Jupiter Aggregator.
                </span>
              </div>
              
              {/* Last Transaction Link */}
              {lastSwapSignature && !isSwapping && (
                <a
                  href={`https://solscan.io/tx/${lastSwapSignature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-xs text-blue-400 hover:text-blue-300 mb-4"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  View last transaction on Solscan
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              
              {/* Swap Button */}
              <Button
                onClick={handleSwap}
                disabled={isSwapping || !swapAmount || parseFloat(swapAmount) < MIN_SWAP_SOL || (swapDirection === 'sol_to_slap' && !swapEstimate)}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold text-lg disabled:opacity-50"
                data-testid="confirm-swap-btn"
              >
                {isSwapping ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {swapProgress?.step || 'SWAPPING'}...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="w-5 h-5 mr-2" />
                    SWAP NOW (REAL TX)
                  </>
                )}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Background */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(#00FF9915 1px, transparent 1px), linear-gradient(90deg, #00FF9915 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Header */}
      <header className="relative z-50 pt-8 pb-4">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={() => navigate('/')}
              className="bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
              data-testid="wallet-back-btn"
            >
              <X className="w-5 h-5 mr-2" />
              Close
            </Button>

            {/* Refresh Button with Spinner */}
            <Button
              onClick={manualRefresh}
              disabled={isRefreshing}
              className="bg-white/10 border border-white/20 text-white hover:bg-white/20"
              data-testid="refresh-balance-btn"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <motion.h1
            className="font-anton text-4xl md:text-5xl uppercase tracking-tight text-center"
            style={{
              background: 'linear-gradient(180deg, #00FF99 0%, #00CC77 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            <Wallet className="inline w-10 h-10 mr-3" />
            MY WALLET
          </motion.h1>
          
          {/* Connection Status */}
          <div className="flex justify-center mt-2">
            <ConnectionIndicator />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 mt-6 space-y-6">
        {/* No Wallet State */}
        {!hasValidWallet && (
          <motion.div
            className="bg-[#0a0a0a] border-2 border-green-500/50 rounded-2xl p-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Zap className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-anton text-2xl text-white mb-2">NO WALLET FOUND</h2>
            <p className="text-white/60 mb-6">Create a Solana wallet to start playing</p>
            <Button
              onClick={handleCreateWallet}
              disabled={isProcessing}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-4 px-8 text-lg"
            >
              {isProcessing ? 'Creating...' : 'CREATE SOLANA WALLET'}
            </Button>
          </motion.div>
        )}

        {/* SECTION A: Balance Display - NOW WITH REAL SOL */}
        {hasValidWallet && (
          <motion.div
            className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-2 border-green-500/50 rounded-2xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-space text-sm text-green-400 uppercase tracking-wider flex items-center gap-2">
                <Coins className="w-4 h-4" />
                Your Balance
              </h2>
              {/* Inline Refresh */}
              <button
                onClick={manualRefresh}
                disabled={isRefreshing}
                className="text-white/60 hover:text-white transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* $SLAP Balance */}
              <div className="bg-black/30 rounded-xl p-4 border border-yellow-500/30">
                <p className="text-white/50 text-xs mb-1">$SLAP</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-anton text-3xl sm:text-4xl text-yellow-400">
                    {Math.floor(credits).toLocaleString()}
                  </span>
                  <Coins className="w-5 h-5 text-yellow-400" />
                </div>
                <p className="text-white/30 text-xs mt-1">Game Currency</p>
              </div>
              
              {/* SOL Balance - REAL FROM BLOCKCHAIN */}
              <div className="bg-black/30 rounded-xl p-4 border border-purple-500/30 relative">
                {isRefreshing && (
                  <div className="absolute top-2 right-2">
                    <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                  </div>
                )}
                <p className="text-white/50 text-xs mb-1">SOL</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-anton text-3xl sm:text-4xl text-purple-400">
                    {solBalance.toFixed(4)}
                  </span>
                  <span className="text-purple-400 text-xl">◎</span>
                </div>
                <p className="text-white/30 text-xs mt-1">
                  {connectionStatus === 'connected' ? 'Live from Mainnet' : 'Connecting...'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* SECTION B: Deposit */}
        {hasValidWallet && (
          <motion.div
            className="bg-[#0a0a0a] border-2 border-blue-500/50 rounded-2xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="font-space text-sm text-blue-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <ArrowDownToLine className="w-4 h-4" />
              Deposit
            </h2>

            {/* QR Code */}
            <div className="flex justify-center mb-4">
              <div className="bg-white rounded-xl p-3">
                {qrCodeDataUrl ? (
                  <img src={qrCodeDataUrl} alt="Wallet QR Code" className="w-32 h-32" />
                ) : (
                  <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                  </div>
                )}
              </div>
            </div>

            {/* Badge */}
            <div className="flex justify-center mb-4">
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full border border-green-500/30">
                ✓ REAL SOLANA ADDRESS
              </span>
            </div>

            {/* Full Address */}
            <div className="bg-black/50 rounded-xl p-4 border border-green-500/30 mb-4">
              <p className="text-green-400 text-xs mb-2 font-bold">YOUR SOLANA ADDRESS (FULL)</p>
              <div className="flex items-start gap-3">
                <code className="text-white font-mono text-sm break-all flex-1 bg-black/30 p-3 rounded-lg">
                  {fullSolanaAddress}
                </code>
                <button
                  onClick={() => handleCopy(fullSolanaAddress, 'Solana Address')}
                  className={`p-3 rounded-xl transition-all flex-shrink-0 ${
                    copied ? 'bg-green-500/30 text-green-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                  }`}
                  data-testid="copy-address-btn"
                >
                  {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-white/40 text-xs mt-2">Short: {shortAddress}</p>
            </div>

            {/* Copy Button */}
            <Button
              onClick={() => handleCopy(fullSolanaAddress, 'Solana Address')}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold text-lg"
              data-testid="copy-address-prominent-btn"
            >
              <Copy className="w-5 h-5 mr-2" />
              COPY SOLANA ADDRESS
            </Button>

            <p className="text-center text-white/50 text-xs mt-3">
              Send SOL to this address. Balance updates automatically every 30 seconds.
            </p>

            {/* PHASE 11: IN-APP SWAP + Jupiter Link */}
            <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
              {/* In-App Swap Button - PRIMARY (Opens Modal, No Redirect) */}
              <Button
                onClick={() => setShowInternalSwapModal(true)}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold text-lg"
                data-testid="open-swap-btn"
              >
                <ArrowRightLeft className="w-5 h-5 mr-2" />
                SWAP SOL → $SLAP (1% Fee)
              </Button>
              
              {/* Live Rate Display */}
              <div className="flex items-center justify-center gap-2 text-xs text-white/60">
                <TrendingUp className={`w-3 h-3 ${isLive ? 'text-green-400' : 'text-yellow-400'}`} />
                <span>1 SOL = {Math.floor(1 / tokenPrice).toLocaleString()} $SLAP</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${isLive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {isLive ? 'LIVE' : 'CACHED'}
                </span>
              </div>
              
              {/* Jupiter External Link - SECONDARY (Fallback) */}
            </div>
          </motion.div>
        )}

        {/* SECTION C: Withdraw - INSTANT TRANSFER */}
        {hasValidWallet && (
          <motion.div
            className="bg-[#0a0a0a] border-2 border-red-500/50 rounded-2xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-space text-sm text-red-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <ArrowUpFromLine className="w-4 h-4" />
              Withdraw SOL
            </h2>

            {/* INSTANT TRANSFER Badge */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 text-green-400 text-sm font-bold">
                <CheckCircle2 className="w-4 h-4" />
                INSTANT TRANSFER
              </div>
              <p className="text-green-200/70 text-xs mt-1">
                Withdrawal is broadcast directly to Solana. No waiting period.
              </p>
            </div>

            {/* Destination Address */}
            <div className="mb-4">
              <label className="text-white/60 text-sm mb-2 block">Destination Solana Address</label>
              <input
                type="text"
                value={withdrawAddress}
                onChange={(e) => setWithdrawAddress(e.target.value)}
                placeholder="Enter Solana wallet address..."
                className="w-full bg-black/50 border border-white/20 rounded-xl p-3 text-white placeholder:text-white/30 focus:border-red-500 focus:outline-none font-mono text-sm"
                data-testid="withdraw-address-input"
              />
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="text-white/60 text-sm mb-2 block">Amount (SOL)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 text-lg">◎</span>
                <input
                  type="number"
                  step="0.0001"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.0000"
                  className="w-full bg-black/50 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white text-xl font-bold placeholder:text-white/30 focus:border-red-500 focus:outline-none"
                  data-testid="withdraw-amount-input"
                />
              </div>
              <p className="text-white/40 text-xs mt-1">Available: {solBalance.toFixed(4)} SOL</p>
            </div>

            {/* Quick Amount Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[0.01, 0.05, 0.1, solBalance].map((val, i) => (
                <button
                  key={i}
                  onClick={() => setWithdrawAmount(Math.max(0, val - 0.0001).toFixed(4))} // Leave some for fees
                  className="py-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/80 text-xs font-bold transition-colors"
                >
                  {i === 3 ? 'MAX' : val}
                </button>
              ))}
            </div>

            {/* Withdraw Button */}
            <Button
              onClick={handleWithdraw}
              disabled={isWithdrawing || !withdrawAmount || !withdrawAddress || parseFloat(withdrawAmount) > solBalance}
              className="w-full py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white font-bold disabled:opacity-50"
              data-testid="withdraw-btn"
            >
              {isWithdrawing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  PROCESSING...
                </>
              ) : (
                'WITHDRAW SOL'
              )}
            </Button>

            <div className="flex items-start gap-2 text-xs text-white/40 mt-4">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-yellow-500" />
              <span>Transaction is signed with your local private key and broadcast immediately. Keep ~0.001 SOL for network fees.</span>
            </div>
          </motion.div>
        )}

        {/* Secret Key Section */}
        {hasValidWallet && displaySecretKey && (
          <motion.div
            className="bg-[#0a0a0a] border border-red-500/30 rounded-2xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-space text-sm text-red-400 uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Secret Key (Backup)
              </h2>
              <button
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="text-white/40 text-xs hover:text-white/60 flex items-center gap-1"
              >
                {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showSecretKey ? 'Hide' : 'Show'}
              </button>
            </div>

            {showSecretKey ? (
              <div className="space-y-3">
                <code className="text-red-400 text-xs block break-all bg-black/50 p-3 rounded-lg">
                  {displaySecretKey}
                </code>
                <Button
                  onClick={() => handleCopy(displaySecretKey, 'Secret Key')}
                  className="w-full py-2 bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 text-sm"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  COPY SECRET KEY
                </Button>
                <p className="text-red-400/60 text-xs">
                  ⚠️ NEVER share this key! Anyone with it can access your wallet.
                </p>
              </div>
            ) : (
              <p className="text-white/40 text-xs">
                Click "Show" to reveal your secret key. Use this to recover your wallet.
              </p>
            )}
          </motion.div>
        )}
      </main>

      {/* In-App Swap Modal - NO REDIRECT */}
      <InternalSwapModal 
        isOpen={showInternalSwapModal} 
        onClose={() => setShowInternalSwapModal(false)} 
      />

      {/* RPC Debug Status Indicator - Bottom Corner */}
      <RpcDebugIndicator />
    </div>
  );
};

export default WalletDashboard;
